import { useState, useEffect } from 'react';

/**
 * Hook para obtener estadísticas en vivo de las colecciones NFT de WhataLab
 * desde SentX API y Kabila API
 * 
 * Colecciones:
 * - WhataKit: 0.0.9219990
 * - WATT: 0.0.3934850
 * - Early Pack: 0.0.3934915
 * 
 * Endpoints utilizados:
 * - SentX: /v1/public/market/floor, /v1/public/token/owners
 * - Kabila: /api/marketplace/manager/nft-collections
 */

const SENTX_API_URL = 'https://api.sentx.io/v1/public';
const SENTX_API_KEY = process.env.REACT_APP_SENTX_KEY;
const HAS_SENTX_KEY = SENTX_API_KEY && SENTX_API_KEY.trim().length > 0;

const KABILA_API_URL = 'https://labs.kabila.app/api/marketplace/manager';

// IDs de las colecciones de WhataLab en Hedera
const COLLECTIONS = {
  WHATAKIT: '0.0.9219990',
  WATT: '0.0.3934850',
  EARLY_PACK: '0.0.3934915'
};

// Valores por defecto (fallback si las APIs fallan)
const DEFAULT_STATS = {
  whatakit: { 
    floorPrice: { sentx: 190, kabila: 250 }, 
    holders: 60 
  },
  watt: { 
    floorPrice: { sentx: 480, kabila: 450 }, 
    holders: 36 
  },
  earlyPack: { 
    floorPrice: { sentx: 1500, kabila: 500 }, 
    holders: 32 
  },
  totalHolders: 128,
  loading: false,
  error: null
};

/**
 * Obtiene el floor price de una colección desde SentX
 */
const fetchSentXFloorPrice = async (tokenId) => {
  if (!HAS_SENTX_KEY) return null;
  try {
    const response = await fetch(
      `${SENTX_API_URL}/market/floor?apikey=${SENTX_API_KEY}&token=${tokenId}`
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data.success ? data.floor : null;
  } catch (error) {
    return null;
  }
};

/**
 * Obtiene el número de holders de una colección desde SentX
 */
const fetchSentXHolders = async (tokenId) => {
  if (!HAS_SENTX_KEY) return null;
  try {
    const response = await fetch(
      `${SENTX_API_URL}/token/owners?apikey=${SENTX_API_KEY}&token=${tokenId}`
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return (data.success && data.ownerCount !== undefined) ? data.ownerCount : null;
  } catch (error) {
    return null;
  }
};

/**
 * Obtiene los datos de todas las colecciones desde Kabila
 */
const fetchKabilaData = async () => {
  try {
    const tokenIds = `${COLLECTIONS.WHATAKIT},${COLLECTIONS.WATT},${COLLECTIONS.EARLY_PACK}`;
    const response = await fetch(
      `${KABILA_API_URL}/nft-collections?tokenIds=${tokenIds}&fields=tokenId,name,minPrice,holders,supply`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Convertir array a objeto por tokenId
    const result = {};
    data.forEach(collection => {
      result[collection.tokenId] = {
        floorPrice: collection.minPrice,
        holders: collection.holders,
        supply: collection.supply
      };
    });
    
    return result;
  } catch (error) {
    console.error('Error fetching Kabila data:', error);
    return null;
  }
};

/**
 * Hook principal para obtener las estadísticas
 */
const useSentXStats = () => {
  const [stats, setStats] = useState({
    ...DEFAULT_STATS,
    loading: true
  });

  useEffect(() => {
    const fetchAllStats = async () => {
      try {
        // Fetch de SentX en paralelo
        const [
          whataKitSentXFloor, 
          wattSentXFloor, 
          earlyPackSentXFloor,
          whataKitHolders,
          wattHolders,
          earlyPackHolders,
          kabilaData
        ] = await Promise.all([
          fetchSentXFloorPrice(COLLECTIONS.WHATAKIT),
          fetchSentXFloorPrice(COLLECTIONS.WATT),
          fetchSentXFloorPrice(COLLECTIONS.EARLY_PACK),
          fetchSentXHolders(COLLECTIONS.WHATAKIT),
          fetchSentXHolders(COLLECTIONS.WATT),
          fetchSentXHolders(COLLECTIONS.EARLY_PACK),
          fetchKabilaData()
        ]);

        // Procesar datos de WhataKit
        const whatakit = {
          floorPrice: {
            sentx: whataKitSentXFloor ?? DEFAULT_STATS.whatakit.floorPrice.sentx,
            kabila: kabilaData?.[COLLECTIONS.WHATAKIT]?.floorPrice ?? DEFAULT_STATS.whatakit.floorPrice.kabila
          },
          holders: whataKitHolders ?? DEFAULT_STATS.whatakit.holders
        };

        // Procesar datos de WATT
        const watt = {
          floorPrice: {
            sentx: wattSentXFloor ?? DEFAULT_STATS.watt.floorPrice.sentx,
            kabila: kabilaData?.[COLLECTIONS.WATT]?.floorPrice ?? DEFAULT_STATS.watt.floorPrice.kabila
          },
          holders: wattHolders ?? DEFAULT_STATS.watt.holders
        };

        // Procesar datos de Early Pack
        const earlyPack = {
          floorPrice: {
            sentx: earlyPackSentXFloor ?? DEFAULT_STATS.earlyPack.floorPrice.sentx,
            kabila: kabilaData?.[COLLECTIONS.EARLY_PACK]?.floorPrice ?? DEFAULT_STATS.earlyPack.floorPrice.kabila
          },
          holders: earlyPackHolders ?? DEFAULT_STATS.earlyPack.holders
        };

        // Total holders = suma de las 3 colecciones
        const totalHolders = whatakit.holders + watt.holders + earlyPack.holders;

        setStats({
          whatakit,
          watt,
          earlyPack,
          totalHolders,
          loading: false,
          error: null
        });

      } catch (error) {
        console.error('Error fetching stats:', error);
        setStats({
          ...DEFAULT_STATS,
          loading: false,
          error: error.message
        });
      }
    };

    fetchAllStats();

    // Actualizar cada 5 minutos
    const interval = setInterval(fetchAllStats, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return stats;
};

export default useSentXStats;
